// Actions the interface uses.
import { on, off, subscribe, unsubscribe } from './actions'

import { logManager, API_LOG_TAG } from '../../logs'

const log = logManager.getLogger('EVENTS')

/**
 * API for Event Emitter plugin.
 * Defines the public end-points exposed by event plugins.
 * @param  {Object} context The sdk instance context.
 * @returns {Object} api     The interface object.
 */
export default function api ({ dispatch }) {
  var api = {}

  /**
   * Add an event listener for the specified event type. The event is emitted by the SDK instance.
   *
   * @public
   * @memberof api
   * @method on
   * @param {string} type The event type for which to add the listener.
   * @param {Function} listener The listener for the event type. The parameters of the listener depend on the event type.
   * @throws {Error} Invalid event type
   * @example
   * // Listen for events of a specific type emitted by the SDK.
   * client.on('dummy:event', function (params) {
   *    // Handle the event.
   * })
   */
  api.on = function (type, listener) {
    log.debug(API_LOG_TAG + 'on: ', type)
    dispatch(on(type, listener))
  }

  /**
   * Removes an event listener for the specified event type. The event is emitted by the SDK instance.
   *
   * @public
   * @memberof api
   * @method off
   * @param {string} type The event type for which to remote the listener.
   * @param {Function} listener The listener to remove.
   * @throws {Error} Invalid event type
   */
  api.off = function (type, listener) {
    log.debug(API_LOG_TAG + 'off: ', type)
    dispatch(off(type, listener))
  }

  /**
   * Adds a global event listener to SDK instance.
   *
   * @public
   * @memberof api
   * @method subscribe
   * @param {Function} listener The event listener to add. The parameters are (type, ...args), where args depend on the event type.
   * @throws {Error} Listener not a function
   */
  api.subscribe = function (listener) {
    log.debug(API_LOG_TAG + 'subscribe')
    dispatch(subscribe(listener))
  }

  /**
   * Removes a global event listener from SDK instance.
   *
   * @public
   * @memberof api
   * @method unsubscribe
   * @param {Function} listener The event listener to remove.
   * @throws {Error} Listener not a function
   */
  api.unsubscribe = function (listener) {
    log.debug(API_LOG_TAG + 'unsubscribe')
    dispatch(unsubscribe(listener))
  }

  return api
}
